import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar, MapPin, Clock, CreditCard, LogOut, CalendarClock, Ban, Mail, FileDown, CheckCheck, Eye, Gift, Copy, Share2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { format } from "date-fns";

export default function MyAccount() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [emails, setEmails] = useState([]);
  const [referral, setReferral] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("10:00");

  const load = async () => {
    try {
      const [b, e, r] = await Promise.all([
        api.get("/customer/bookings"),
        api.get("/customer/emails").catch(() => ({ data: [] })),
        api.get("/customer/referral").catch(() => ({ data: null })),
      ]);
      setBookings(b.data);
      setEmails(e.data || []);
      setReferral(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const cancel = async (id) => {
    if (!window.confirm(t("account_page.cancel_confirm"))) return;
    try {
      const r = await api.post(`/customer/bookings/${id}/cancel`);
      toast.success(`${r.data.note}. ${t("account_page.refund_note", { amount: r.data.refund_amount })}.`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const reschedule = async () => {
    try {
      const r = await api.post(`/customer/bookings/${rescheduling.id}/reschedule`, { new_date: newDate, new_start_time: newTime });
      toast.success(r.data.note);
      setRescheduling(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const handleLogout = () => { logout(); nav("/"); };

  const upcoming = bookings.filter((b) => b.status !== "Cancelled");
  const past = bookings.filter((b) => b.status === "Cancelled");

  return (
    <div className="min-h-screen bg-brand-appBg">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-logo-link"><Logo /></Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-700">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="account-logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">{t("account_page.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("account_page.subtitle")}</p>

        <div className="mt-6">
          <Link to="/book">
            <Button className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full" data-testid="account-book-again">{t("account_page.book_again")}</Button>
          </Link>
        </div>

        {referral && <ReferralCard referral={referral} t={t} />}

        <Section title={t("account_page.upcoming")}>
          {upcoming.length === 0 && <Empty text={t("account_page.empty_upcoming")} />}
          {upcoming.map((b) => <BookingCard key={b.id} t={t} b={b} onCancel={() => cancel(b.id)} onReschedule={() => { setRescheduling(b); setNewDate(b.date); setNewTime(b.start_time); }} />)}
        </Section>

        {past.length > 0 && (
          <Section title={t("account_page.past")}>
            {past.map((b) => <BookingCard key={b.id} t={t} b={b} muted />)}
          </Section>
        )}

        <Section title={t("account_page.emails_title")}>
          {emails.length === 0 && <Empty text={t("account_page.emails_empty")} />}
          {emails.length > 0 && (
            <div className="bg-white rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
              {emails.map((e) => <EmailRow key={e.id} email={e} t={t} />)}
            </div>
          )}
        </Section>
      </main>

      <Dialog open={!!rescheduling} onOpenChange={(o) => !o && setRescheduling(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("account_page.rs_title")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">{t("account_page.rs_fee_note")}</div>
            <div>
              <Label>{t("account_page.rs_new_date")}</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("account_page.rs_new_time")}</Label>
              <Select value={newTime} onValueChange={setNewTime}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00"].map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescheduling(null)}>{t("account_page.cancel_label")}</Button>
            <Button onClick={reschedule} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="account-reschedule-confirm">{t("account_page.rs_confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="bg-white rounded-xl ring-1 ring-slate-200 p-8 text-center text-sm text-slate-500">{text}</div>;
}

function BookingCard({ b, onCancel, onReschedule, muted, t }) {
  let dateText = b.date;
  try { dateText = format(new Date(b.date), "EEEE, MMM d, yyyy"); } catch { /* ignore */ }
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const invoiceUrl = b.invoice_no ? `${apiBase}/invoices/${b.invoice_no}/pdf` : null;
  const downloadInvoice = async () => {
    try {
      const token = localStorage.getItem("trustline_token");
      const resp = await fetch(invoiceUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Could not load invoice"); }
  };
  return (
    <div className={`bg-white rounded-xl ring-1 ring-slate-200 p-5 ${muted ? "opacity-70" : ""}`} data-testid={`account-booking-${b.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-[#0F172A]">{b.service_type}</h3>
            <StatusBadge status={b.status} />
          </div>
          <div className="text-sm text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {dateText}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {b.start_time}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {b.address}, {b.city}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{b.frequency}</div>
          <div className="font-heading text-xl font-bold text-[#1B3A6B]">€{b.amount?.toFixed(2)}</div>
          <div className="text-xs text-slate-500 inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> {b.payment_status}</div>
        </div>
      </div>
      {!muted && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onReschedule} data-testid={`account-reschedule-${b.id}`}>
            <CalendarClock className="w-4 h-4 mr-1" /> {t("account_page.reschedule")}
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`account-cancel-${b.id}`}>
            <Ban className="w-4 h-4 mr-1" /> {t("account_page.cancel")}
          </Button>
          {invoiceUrl && (
            <Button size="sm" variant="outline" onClick={downloadInvoice} data-testid={`account-invoice-${b.id}`}>
              <FileDown className="w-4 h-4 mr-1" /> {t("account_page.download_invoice")}
            </Button>
          )}
        </div>
      )}
      {b.status === "Pending Confirmation" && (
        <div className="mt-3 text-xs bg-amber-50 text-amber-900 rounded-md p-2.5 leading-relaxed">
          {t("account_page.pending_note")}
        </div>
      )}
      {b.cancellation && (
        <div className="mt-3 text-xs bg-rose-50 text-rose-800 rounded-md p-2">
          {b.cancellation.note} · {t("account_page.refund_note", { amount: b.cancellation.refund_amount })}
        </div>
      )}
    </div>
  );
}

function EmailRow({ email, t }) {
  const ICONS = {
    opened: <Eye className="w-3.5 h-3.5 text-emerald-600" />,
    delivered: <CheckCheck className="w-3.5 h-3.5 text-emerald-600" />,
    sent: <Mail className="w-3.5 h-3.5 text-slate-500" />,
    queued: <Mail className="w-3.5 h-3.5 text-slate-400" />,
    failed: <Mail className="w-3.5 h-3.5 text-rose-500" />,
    skipped: <Mail className="w-3.5 h-3.5 text-slate-400" />,
    bounced: <Mail className="w-3.5 h-3.5 text-rose-500" />,
  };
  const COLOR = {
    opened: "bg-emerald-100 text-emerald-700",
    delivered: "bg-emerald-50 text-emerald-700",
    sent: "bg-slate-100 text-slate-600",
    queued: "bg-slate-100 text-slate-500",
    failed: "bg-rose-100 text-rose-700",
    skipped: "bg-amber-100 text-amber-800",
    bounced: "bg-rose-100 text-rose-700",
  };
  let dt = email.created_at;
  try { dt = format(new Date(email.created_at), "MMM d, yyyy · HH:mm"); } catch { /* ignore */ }
  return (
    <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`account-email-${email.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#0F172A] truncate">{email.subject}</div>
        <div className="text-xs text-slate-500 mt-0.5">{dt}</div>
      </div>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${COLOR[email.status] || "bg-slate-100 text-slate-600"}`}>
        {ICONS[email.status] || ICONS.sent}
        {t(`account_page.email_status.${email.status}`, { defaultValue: email.status })}
      </span>
    </div>
  );
}

function ReferralCard({ referral, t }) {
  const code = referral.referral_code || "";
  const reward = referral.reward_per_referral_eur ?? 10;
  const credit = Number(referral.store_credit_eur || 0);
  const referrals = referral.referrals || [];
  const shareText = t("account_page.referral.share_text", {
    reward,
    code,
    defaultValue: `Get €${reward} off your first Trustline cleaning with my code ${code}. https://trustlineservices.nl/book`,
  });
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("account_page.referral.copied", { defaultValue: "Code copied!" }));
    } catch (_e) { toast.error("Clipboard not available"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Trustline referral", text: shareText }); return; } catch (_e) { /* fall through to clipboard */ }
    }
    await navigator.clipboard.writeText(shareText);
    toast.success(t("account_page.referral.share_copied", { defaultValue: "Message copied to clipboard!" }));
  };
  return (
    <section className="mt-6 rounded-2xl bg-gradient-to-br from-[#1B3A6B] via-[#22497F] to-[#1B3A6B] text-white p-6 md:p-7 shadow-sm" data-testid="referral-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#9BD478]">
            {t("account_page.referral.eyebrow", { defaultValue: "Refer & Earn" })}
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold mt-1">
            {t("account_page.referral.title", { reward, defaultValue: `Give €${reward}, get €${reward}` })}
          </h2>
          <p className="text-sm text-slate-200 mt-1.5 leading-relaxed">
            {t("account_page.referral.description", { reward, defaultValue: `Share your code — friends get €${reward} off their first cleaning, and you get €${reward} back as store credit once their job is completed.` })}
          </p>

          {/* Credit balance */}
          <div className="mt-4 rounded-lg bg-white/10 px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-white/70">
                {t("account_page.referral.credit_label", { defaultValue: "Your store credit" })}
              </div>
              <div className="text-2xl font-bold mt-0.5" data-testid="referral-credit">€{credit.toFixed(2)}</div>
            </div>
            <div className="text-[11px] text-white/70 text-right leading-snug max-w-[180px]">
              {t("account_page.referral.credit_hint", { defaultValue: "Auto-applied to your next booking." })}
            </div>
          </div>

          {/* Code + actions */}
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <div className="bg-white/10 rounded-lg px-3 py-2 font-mono tracking-widest text-lg font-bold" data-testid="referral-code">{code}</div>
            <Button size="sm" onClick={copyCode} className="rounded-full bg-white text-[#1B3A6B] hover:bg-slate-100 font-semibold" data-testid="referral-copy-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> {t("account_page.referral.copy", { defaultValue: "Copy" })}
            </Button>
            <Button size="sm" onClick={share} className="rounded-full bg-[#5BA83F] hover:bg-[#4A8A33] text-white font-semibold" data-testid="referral-share-btn">
              <Share2 className="w-3.5 h-3.5 mr-1" /> {t("account_page.referral.share", { defaultValue: "Share" })}
            </Button>
          </div>

          {/* History */}
          {referrals.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-xs uppercase tracking-wide font-semibold text-white/70 mb-2">
                {t("account_page.referral.history_title", { defaultValue: "Your referrals" })}
              </div>
              <ul className="space-y-1.5 text-sm" data-testid="referral-history">
                {referrals.slice(0, 5).map((r) => (
                  <li key={r.booking_id} className="flex items-center justify-between gap-2 bg-white/5 rounded px-3 py-1.5">
                    <span className="text-white/90 truncate">{r.friend_name || r.friend_email_masked}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.credited ? "bg-[#5BA83F]/30 text-[#9BD478]" : "bg-white/10 text-white/70"}`}>
                      {r.credited
                        ? t("account_page.referral.credited", { reward, defaultValue: `+€${reward}` })
                        : t("account_page.referral.pending", { defaultValue: "pending" })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

